import React, { useState, useEffect, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { cloneDeep } from 'lodash'

import Modal from 'components/Modal/Modal'
import Header from 'components/Modal/components/Header/Header'
import Body from 'components/Modal/components/Body/Body'
import { FinalScreenTab } from 'components/ModalTabs/FinalScreenTab'

import { ProjectContext } from 'pages/Editor/contexts/projectContext'

import StopPanels, { useStopPanes, VALIDATION_WARNING_TYPES } from '../components/StopPanels/StopPanels'

import { validateTabs, TABS, TABS_ARRAY, TILES_FIELDS, treasureHuntProjectGetData, getFormattedPassword } from './utils'
import { Playground, Tiles } from './views'

import './TreasureHuntModal.scss'

export const TreasureHuntModal = ({ methods, data: { structure, colorTheme, id, pages } }) => {
    const { t } = useTranslation()
    const [tmpStructure, setTmpStructure] = useState(cloneDeep(structure))
    const [activeTab, setActiveTab] = useState(TABS.playground)
    const [tabsValidationResult, setTabValidationResult] = useState({
        isCurrentTab: false,
        errors: { playground: {}, tiles: {}, finalScreen: {} },
    })

    const {
        isShowQuitAccept,
        onShowQuitAccept,
        validationWarningType,
        setValidationWarningType,
        onClosePanels,
    } = useStopPanes()

    const { isMultiplayerGame } = useContext(ProjectContext)

    useEffect(() => {
        if (tmpStructure.playground.layout && Object.keys(tmpStructure.playground.layout).length) {
            const { tileList } = tmpStructure.tiles
            const tilesCount = tmpStructure.playground.layout.value
            if (tileList.length === 0) {
                onUpdateStructure('tiles')({
                    [TILES_FIELDS.tileList]: treasureHuntProjectGetData(tilesCount, colorTheme),
                })
            } else if (tileList.length < tilesCount) {
                onUpdateStructure('tiles')({
                    [TILES_FIELDS.tileList]: [
                        ...tileList,
                        ...treasureHuntProjectGetData(tilesCount - tileList.length, colorTheme),
                    ],
                })
            } else if (tilesCount < tileList.length) {
                onUpdateStructure('tiles')({
                    [TILES_FIELDS.tileList]: [...tileList.slice(0, tilesCount)],
                })
            }
        }
    }, [tmpStructure.playground.layout])

    const onSave = async () => {
        const validationResult = validateTabs(tmpStructure, tabsValidationResult)

        if (!validationResult.hasError) {
            let newTmpStructure = cloneDeep(tmpStructure)
            newTmpStructure.tiles[TILES_FIELDS.tileList] = tmpStructure.tiles[TILES_FIELDS.tileList].map(tile => ({
                ...tile,
                password: getFormattedPassword(tile.password),
            }))
            await methods.save({
                [id]: {
                    data: {
                        struct: newTmpStructure,
                    },
                },
            })
            onClose()
        } else {
            setTabValidationResult({ isCurrentTab: false, errors: validationResult.errors })
            setValidationWarningType(VALIDATION_WARNING_TYPES.COMMON)
        }
    }

    const onClose = () => {
        methods.closeModal()
    }

    const onUpdateStructure = type => newFields => {
        setTmpStructure(prevState => {
            return {
                ...prevState,
                [type]: {
                    ...prevState[type],
                    ...newFields,
                },
            }
        })
    }

    const onChangeTab = useCallback(
        tabId => {
            const validationResult = validateTabs(tmpStructure, tabsValidationResult, activeTab)
            if (validationResult.activeTabHasError) {
                setTabValidationResult({
                    isCurrentTab: validationResult.activeTabHasError,
                    errors: { ...tabsValidationResult.errors, ...validationResult.errors },
                })
                setValidationWarningType(VALIDATION_WARNING_TYPES.ACTIVE_TAB)
            } else {
                setTabValidationResult({
                    isCurrentTab: validationResult.activeTabHasError,
                    errors: { ...tabsValidationResult.errors, ...validationResult.errors },
                })
                setActiveTab(tabId)
            }
        },
        [tmpStructure, activeTab],
    )

    return (
        <Modal closeOnOverlayClick={false} closeOnEsc={false}>
            <Header title={t('Treasure Hunt')} onClose={onShowQuitAccept} />
            <Body
                sideNav={{
                    items: TABS_ARRAY.filter(tab => (isMultiplayerGame ? !tab.isHideFromMultiplayer : true)),
                    activeItemId: activeTab,
                    onChangeItem: onChangeTab,
                    analyticsBlockName: 'TREASURE_HUNT',
                }}
                onSave={onSave}
            >
                <div className="treasure-hunt-modal-wrapper">
                    <div className="treasure-hunt-modal-wrapper__body">
                        {activeTab === TABS.playground && (
                            <Playground
                                structure={tmpStructure}
                                colorTheme={colorTheme}
                                validateErrors={tabsValidationResult.errors}
                                methods={{
                                    ...methods,
                                    updateStructure: onUpdateStructure('playground'),
                                }}
                            />
                        )}
                        {activeTab === TABS.tiles && (
                            <Tiles
                                pages={pages}
                                structure={tmpStructure}
                                colorTheme={colorTheme}
                                validateErrors={tabsValidationResult.errors.tiles}
                                methods={{ ...methods, updateStructure: onUpdateStructure('tiles') }}
                            />
                        )}
                        {activeTab === TABS.finalScreen && (
                            <FinalScreenTab
                                structure={tmpStructure}
                                validateErrors={tabsValidationResult.errors}
                                methods={{ ...methods, updateStructure: onUpdateStructure('finalScreen') }}
                                isDisabledDelete
                            />
                        )}
                    </div>
                </div>
            </Body>

            <StopPanels
                isShowQuitAccept={isShowQuitAccept}
                validationWarningType={validationWarningType}
                onBack={onClosePanels}
                onQuit={onClose}
            />
        </Modal>
    )
}
